import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { ListTodo, Plus } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import RichTextEditor from '@/components/RichTextEditor';

const PRIORITIES = ['Low', 'Medium', 'High', 'Urgent'];

/**
 * Create one or more To-Dos from a meeting note.
 * The user controls every field - defaults pre-fill from the note, then they
 * edit title / description / priority / due date / assignee, and pick one
 * section per project where the todo should land. One POST spawns N tasks.
 */
export default function CreateTodoFromNoteDialog({ open, onOpenChange, note, users, onCreated }) {
  const [scope, setScope] = useState({ projects: [] });
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    title: '',
    description: '',
    priority: 'Medium',
    due_date: '',
    assigned_to: '',
  });
  // per-project state: { [project_id]: { selected: bool, section_id: '' } }
  const [projectState, setProjectState] = useState({});
  const [saving, setSaving] = useState(false);

  
  useEffect(() => {
    if (!open || !note?.id) return;
    setLoading(true);
    // Prefill defaults from the note
    const plain = (note.notes || '')
      .replace(/<[^>]+>/g, '')
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/\s+/g, ' ')
      .trim();
    const firstLine = plain.split('\n')[0].slice(0, 100);
    setForm({
      title: firstLine || 'Follow up on meeting note',
      description: note.notes || '',
      priority: 'Medium',
      due_date: '',
      assigned_to: '',
    });
    api.get(`/meeting-notes/${note.id}/scope`)
      .then(r => {
        setScope(r.data);
        const state = {};
        (r.data.projects || []).forEach(p => {
          // Default: select the project if it has at least one active section
          const firstSection = (p.sections || []).find(s => s.status !== 'Cancelled');
          state[p.id] = {
            selected: !!firstSection,
            section_id: firstSection?.id || '',
          };
        });
        setProjectState(state);
      })
      .catch(() => toast.error('Failed to load project scope'))
      .finally(() => setLoading(false));
  }, [open, note?.id, note?.notes]);
  

  const toggleProject = (pid) => {
    setProjectState(prev => ({
      ...prev,
      [pid]: { ...prev[pid], selected: !prev[pid].selected },
    }));
  };

  const updateSection = (pid, sid) => {
    setProjectState(prev => ({
      ...prev,
      [pid]: { ...prev[pid], section_id: sid },
    }));
  };

  const selectedCount = Object.values(projectState).filter(s => s.selected).length;

  const save = async () => {
    if (!form.title.trim()) return toast.error('Title is required');
    if (selectedCount === 0) return toast.error('Pick at least one project');
    const entries = Object.entries(projectState)
      .filter(([, s]) => s.selected)
      .map(([pid, s]) => ({ project_id: pid, contract_section_id: s.section_id }));
    const missingSection = entries.find(e => !e.contract_section_id);
    if (missingSection) return toast.error('Every selected project needs a section picked');
    setSaving(true);
    try {
      const r = await api.post(`/meeting-notes/${note.id}/todos`, {
        title: form.title.trim(),
        description: form.description,
        priority: form.priority,
        due_date: form.due_date || null,
        assigned_to: form.assigned_to || null,
        entries,
      });
      toast.success(`Created ${r.data.created} to-do${r.data.created === 1 ? '' : 's'}`);
      if (onCreated) onCreated(r.data);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create to-dos');
    } finally {
      setSaving(false);
    }
  };

  const staff = (users || []).filter(u => u.is_active !== false && u.role !== 'Client');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="create-todo-from-note-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ListTodo className="h-5 w-5 text-primary" />
            Create To-Do{scope.projects?.length > 1 ? 's' : ''} from Meeting Note
          </DialogTitle>
          <p className="text-xs text-muted-foreground">
            We pre-filled the fields from the note. Edit anything, then pick which projects get a to-do.
          </p>
        </DialogHeader>

        {loading ? (
          <p className="text-xs text-muted-foreground py-8 text-center">Loading project scope…</p>
        ) : (
          <div className="space-y-3">
            {/* Title */}
            <div>
              <Label className="text-xs text-muted-foreground">Title *</Label>
              <Input
                value={form.title}
                onChange={e => setForm({ ...form, title: e.target.value })}
                placeholder="Follow up with architect on finishes"
                className="bg-background border-border"
                data-testid="todo-from-note-title"
              />
            </div>

            {/* Description */}
            <div>
              <Label className="text-xs text-muted-foreground">Description</Label>
              <RichTextEditor
                placeholder="Details…"
                value={form.description}
                onChange={v => setForm({ ...form, description: v })}
                data-testid="todo-from-note-description"
              />
            </div>

            {/* Priority / Due / Assignee */}
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label className="text-xs text-muted-foreground">Priority</Label>
                <Select value={form.priority} onValueChange={v => setForm({ ...form, priority: v })}>
                  <SelectTrigger className="bg-background border-border" data-testid="todo-from-note-priority">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    {PRIORITIES.map(p => <SelectItem key={p} value={p} className="text-sm">{p}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Due date</Label>
                <Input
                  type="date"
                  value={form.due_date}
                  onChange={e => setForm({ ...form, due_date: e.target.value })}
                  className="bg-background border-border"
                  data-testid="todo-from-note-due-date"
                />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Assignee</Label>
                <Select value={form.assigned_to || 'none'} onValueChange={v => setForm({ ...form, assigned_to: v === 'none' ? '' : v })}>
                  <SelectTrigger className="bg-background border-border" data-testid="todo-from-note-assignee">
                    <SelectValue placeholder="Unassigned" />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border max-h-60">
                    <SelectItem value="none" className="text-sm text-muted-foreground">Unassigned</SelectItem>
                    {staff.map(u => <SelectItem key={u.id} value={u.id} className="text-sm">{u.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Per-project scope + section picker */}
            <div>
              <Label className="text-xs text-muted-foreground">
                Projects & sections ({selectedCount} of {scope.projects?.length || 0} selected)
              </Label>
              {(!scope.projects || scope.projects.length === 0) ? (
                <p className="text-xs text-muted-foreground py-2">No projects are in this note&apos;s scope.</p>
              ) : (
                <div className="border border-border rounded-md p-2 space-y-2 bg-background max-h-80 overflow-y-auto" data-testid="todo-from-note-projects">
                  {scope.projects.map(p => {
                    const ps = projectState[p.id] || { selected: false, section_id: '' };
                    const activeSections = (p.sections || []).filter(s => s.status !== 'Cancelled');
                    return (
                      <div key={p.id} className="border border-border rounded p-2 bg-card">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <Checkbox
                            checked={ps.selected}
                            onCheckedChange={() => toggleProject(p.id)}
                            disabled={activeSections.length === 0}
                            data-testid={`todo-from-note-project-${p.id}`}
                          />
                          <span className="font-mono text-xs text-primary">{p.project_number}</span>
                          <span className="text-xs flex-1 truncate">{p.name}</span>
                          {p.status && <Badge variant="outline" className="text-xs">{p.status}</Badge>}
                        </label>
                        {ps.selected && (
                          <div className="mt-2 pl-6">
                            {activeSections.length === 0 ? (
                              <p className="text-xs text-destructive">This project has no active sections. Can&apos;t create a to-do.</p>
                            ) : (
                              <Select value={ps.section_id} onValueChange={v => updateSection(p.id, v)}>
                                <SelectTrigger className="bg-background border-border h-8 text-xs" data-testid={`todo-from-note-section-${p.id}`}>
                                  <SelectValue placeholder="Pick a section…" />
                                </SelectTrigger>
                                <SelectContent className="bg-card border-border">
                                  {activeSections.map(s => (
                                    <SelectItem key={s.id} value={s.id} className="text-xs">
                                      {s.section_number ? `${s.section_number} - ${s.section_name}` : s.section_name}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            )}
                          </div>
                        )}
                        {!ps.selected && activeSections.length === 0 && (
                          <p className="text-xs text-muted-foreground pl-6 mt-1">No active sections</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        <DialogFooter className="flex flex-row justify-end gap-2">
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            size="sm"
            onClick={save}
            disabled={saving || loading || selectedCount === 0 || !form.title.trim()}
            className="bg-primary text-primary-foreground"
            data-testid="save-todo-from-note-btn"
          >
            <Plus className="h-3 w-3 mr-1" />
            {saving ? 'Creating…' : `Create ${selectedCount || ''} To-Do${selectedCount === 1 ? '' : 's'}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
